/**
 * Where a member of staff goes back to after signing in (ORDR-63).
 *
 * THIS IS AN OPEN-REDIRECT BOUNDARY. `next=` is part of the address, so anybody
 * can write one into a link. Kept only when it is a same-origin RELATIVE path:
 *
 * - it must start with exactly one `/`. `//host` is protocol-relative and
 *   `https://host` is absolute; both leave the origin;
 * - it may hold no backslash, because browsers read `/\host` as `//host`;
 * - it may hold no whitespace or control character, because browsers strip tab
 *   and newline from a URL, which turns `/<TAB>/host` back into `//host`;
 * - resolved against an origin, it must still be on that origin;
 * - it may not be a sign-in screen, which would loop.
 *
 * Anything else is IGNORED, not repaired, and the member of staff lands on the
 * kitchen. The value is checked twice: when `/login` stores it and again when
 * `/auth/callback` reads it back from sessionStorage, because storage is not a
 * trust boundary either.
 */

import { authScreenFor, LOGIN_PATH } from "./paths.js";

export const DEFAULT_RETURN_PATH = "/kitchen";

/** A placeholder origin, only to prove resolution cannot change the origin. */
const PROBE_ORIGIN = "https://ordering.invalid";

const SPACE = 0x20;
const DELETE = 0x7f;

/** True for any character at or below space, or DEL. Code points, not a regex literal. */
function hasWhitespaceOrControl(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code <= SPACE || code === DELETE) {
      return true;
    }
  }
  return false;
}

export function safeReturnPath(value: string | null | undefined): string {
  if (typeof value !== "string" || value === "") {
    return DEFAULT_RETURN_PATH;
  }
  if (!value.startsWith("/") || value.startsWith("//")) {
    return DEFAULT_RETURN_PATH;
  }
  if (value.includes("\\") || hasWhitespaceOrControl(value)) {
    return DEFAULT_RETURN_PATH;
  }
  let resolved: URL;
  try {
    resolved = new URL(value, PROBE_ORIGIN);
  } catch {
    return DEFAULT_RETURN_PATH;
  }
  if (resolved.origin !== PROBE_ORIGIN) {
    return DEFAULT_RETURN_PATH;
  }
  if (authScreenFor(resolved.pathname) !== null) {
    return DEFAULT_RETURN_PATH;
  }
  return value;
}

/** The return path `/login?next=` names, validated. */
export function returnPathFromSearch(search: string): string {
  return safeReturnPath(new URLSearchParams(search).get("next"));
}

/** The sign-in address that brings the member of staff back to `path`. */
export function loginHref(path: string): string {
  return `${LOGIN_PATH}?next=${encodeURIComponent(safeReturnPath(path))}`;
}
